import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

const EXPENSE_CATEGORIES = [
  'Kira',
  'Fatura',
  'Temizlik',
  'Pazarlama',
  'Ekipman',
  'Market',
  'Bakım',
  'Vergi',
  'Diğer',
];

const PAYMENT_METHODS = [
  { label: 'Nakit', value: 'CASH' },
  { label: 'Kart', value: 'CARD' },
  { label: 'Havale', value: 'BANK_TRANSFER' },
];

function getTodayISO() {
  return new Date().toISOString().slice(0, 10);
}

const INITIAL_FORM = {
  category: '',
  title: '',
  amount: '',
  note: '',
  dueDate: getTodayISO(),
  isPaid: true,
  paidDate: getTodayISO(),
  paymentMethod: 'CASH',
};

function mapExpenseCategory(value) {
  switch (value) {
    case 'Kira':
      return 'RENT';
    case 'Fatura':
      return 'UTILITIES';
    case 'Temizlik':
      return 'CLEANING';
    case 'Pazarlama':
      return 'MARKETING';
    case 'Ekipman':
      return 'EQUIPMENT';
    case 'Market':
      return 'SHOPPING';
    case 'Bakım':
      return 'MAINTENANCE';
    case 'Vergi':
      return 'TAX';
    default:
      return 'OTHER';
  }
}

export default function AddExpenseModal({ open, onClose, onSubmit }) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';

  const [form, setForm] = React.useState(INITIAL_FORM);
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setForm({
        ...INITIAL_FORM,
        dueDate: getTodayISO(),
        paidDate: getTodayISO(),
      });
      setErrors({});
      setSubmitting(false);
    }
  }, [open]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleTogglePaid = (e) => {
    const checked = e.target.checked;
    setForm((prev) => ({
      ...prev,
      isPaid: checked,
    }));
    setErrors((prev) => ({
      ...prev,
      dueDate: '',
      paidDate: '',
      paymentMethod: '',
    }));
  };

  const validate = () => {
    const next = {};

    if (!form.category) {
      next.category = 'Gider türü seç.';
    }

    if (!form.amount || Number(form.amount) <= 0) {
      next.amount = 'Geçerli bir tutar gir.';
    }

    if (form.isPaid) {
      if (!form.paidDate) {
        next.paidDate = 'Ödeme tarihi gerekli.';
      }
      if (!form.paymentMethod) {
        next.paymentMethod = 'Ödeme yöntemi gerekli.';
      }
    } else {
      if (!form.dueDate) {
        next.dueDate = 'Vade tarihi gerekli.';
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      title: form.title?.trim() || null,
      category: mapExpenseCategory(form.category),
      amountTry: Number(form.amount || 0),
      note: form.note?.trim() || null,
      status: form.isPaid ? 'PAID' : 'PENDING',
      dueDate: form.isPaid ? null : form.dueDate || null,
      paidDate: form.isPaid ? form.paidDate || null : null,
      paymentMethod: form.isPaid ? form.paymentMethod || null : null,
    };

    try {
      setSubmitting(true);
      await onSubmit?.(payload);
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '22px',
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MDBox
            sx={{
              width: 38,
              height: 38,
              borderRadius: '14px',
              display: 'grid',
              placeItems: 'center',
              background: alpha(theme.palette.success.main, isDark ? 0.18 : 0.1),
              color: theme.palette.success.main,
            }}
          >
            <Icon fontSize="small">receipt_long</Icon>
          </MDBox>

          <MDBox>
            <MDTypography variant="h6" fontWeight="bold">
              Gider ekle
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Muhasebeye yeni gider kaydı ekle
            </MDTypography>
          </MDBox>
        </MDBox>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: theme.palette.divider }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox checked={form.isPaid} onChange={handleTogglePaid} disabled={submitting} />
              }
              label="Bu gider zaten ödendi"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Gider türü"
              value={form.category}
              onChange={handleChange('category')}
              size="small"
              error={!!errors.category}
              helperText={errors.category}
            >
              <MenuItem value="">Seç</MenuItem>
              {EXPENSE_CATEGORIES.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            {form.isPaid ? (
              <TextField
                fullWidth
                type="date"
                label="Ödeme tarihi"
                value={form.paidDate}
                onChange={handleChange('paidDate')}
                size="small"
                error={!!errors.paidDate}
                helperText={errors.paidDate}
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <TextField
                fullWidth
                type="date"
                label="Vade tarihi"
                value={form.dueDate}
                onChange={handleChange('dueDate')}
                size="small"
                error={!!errors.dueDate}
                helperText={errors.dueDate}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Gider başlığı"
              value={form.title}
              onChange={handleChange('title')}
              size="small"
              placeholder="Örn: Mart ayı kira ödemesi"
              helperText="Boş bırakırsan sistem kategori adını kullanır."
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              label="Tutar"
              value={form.amount}
              onChange={handleChange('amount')}
              size="small"
              error={!!errors.amount}
              helperText={errors.amount}
              InputProps={{
                startAdornment: <InputAdornment position="start">₺</InputAdornment>,
              }}
            />
          </Grid>

          {form.isPaid && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Ödeme yöntemi"
                value={form.paymentMethod}
                onChange={handleChange('paymentMethod')}
                size="small"
                error={!!errors.paymentMethod}
                helperText={errors.paymentMethod}
              >
                {PAYMENT_METHODS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Açıklama"
              value={form.note}
              onChange={handleChange('note')}
              size="small"
              multiline
              minRows={3}
              placeholder="Opsiyonel not..."
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800 }}
        >
          İptal
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={<Icon fontSize="small">save</Icon>}
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 800,
            boxShadow: 'none',
          }}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
}
